import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';

import { overlayBunja } from '@flipster/overlay';
import { css } from '@flipster/styled-system/css';
import { OverlayModal } from '@flipster/ui';
import segment from '@flipster/utils/segment';
import { WalletEvent } from '@flipster/utils/segment/events/wallet';

import { SegmentTrack } from '../../../misc/segment/segment-track';
import { accountValidationBunja } from '../shared/account-validation/state';
import {
  WalletModalOptions,
  WalletModalOptionsContext,
  WalletModalTypeContext,
  walletModalBunja,
} from '../shared/core/state';
import { SelectAssetContent } from '../shared/select-asset/content';
import { Header as SelectAssetHeader } from '../shared/select-asset/header';
import { DepositContent } from './content';
import { Header as DepositHeader } from './header';

interface DepositModalProps {
  options?: WalletModalOptions;
}
const DepositModal = ({ options = { defaultValues: {} } }: DepositModalProps) => {
  return (
    <WalletModalTypeContext value='deposit'>
      <WalletModalOptionsContext value={options}>
        <DepositModalImpl />
      </WalletModalOptionsContext>
    </WalletModalTypeContext>
  );
};

const DepositModalImpl = () => {
  const { selectedCurrencyAtom } = useBunja(walletModalBunja);
  const [selectedCurrency, setSelectedCurrency] = useAtom(selectedCurrencyAtom);
  return (
    <OverlayModal
      size='small'
      className={css(selectedCurrency == null && { h: 640 })}
      header={selectedCurrency == null ? <SelectAssetHeader /> : <DepositHeader />}
    >
      {selectedCurrency == null && (
        <>
          <SegmentTrack name={WalletEvent.DepositSelectCryptoPageView} />
          <SelectAssetContent
            onClickCurrency={(currency, ctx) => {
              setSelectedCurrency(currency);
              segment.track(WalletEvent.DepositCryptoCurrencyClick, {
                currency,
                isRecommended: ctx.isRecommended,
                highlights: ctx.highlights,
                list_order: ctx.listOrder,
                search_word: ctx.keyword,
              });
            }}
          />
        </>
      )}
      {selectedCurrency != null && <DepositContent />}
    </OverlayModal>
  );
};

export const openDepositModalBunja = bunja(() => {
  const overlay = bunja.use(overlayBunja);
  const { checkKycAndOpenAlertIfNeeded, checkUserBlockAndOpenAlertIfNeeded } = bunja.use(accountValidationBunja);
  async function openDepositModal(options: WalletModalOptions = { defaultValues: {} }) {
    if (!(await checkKycAndOpenAlertIfNeeded())) return;
    if (!(await checkUserBlockAndOpenAlertIfNeeded('deposit'))) return;
    overlay.open(() => <DepositModal options={options} />);
  }
  return { openDepositModal };
});
openDepositModalBunja.debugLabel = 'openDepositModalBunja';

export const useDepositModal = () => {
  const { openDepositModal } = useBunja(openDepositModalBunja);
  return openDepositModal;
};
